import { useMemo } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Clipboard,
  AlertCircle,
  BookOpen,
  Book,
  Plus,
  LayoutGrid,
  Map,
  Radar,
  Radio,
  CloudSun,
  Plane,
  CalendarClock,
  ListChecks,
  User,
  BookMarked,
  Link as LinkIcon,
  Settings,
  Shield,
  CreditCard,
  LogOut,
  Globe2,
  BookText,
  IdCard,
  Aperture,
  ShieldAlert,
  CalendarX,
  Trees,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import SubscriptionGate from "./SubscriptionGate";

type TabKey =
  | "briefings"
  | "radar"
  | "notams"
  | "logbuch"
  | "ndfilter"
  | "einstellungen";

/* Untere Tab-Leiste — exakt wie die native App (client/navigation/
   MainTabNavigator.tsx): sechs Tabs in identischer Reihenfolge mit den
   gleichen Icons. Die "Einstellungen" führt – wie in der App – auf den
   Einstellungs-Hub (/einstellungen), kein separates "Mehr"-Sheet. */
const TABS: {
  key: TabKey;
  to: string;
  label: string;
  icon: LucideIcon;
}[] = [
  { key: "briefings", to: "/briefings", label: "Briefings", icon: Clipboard },
  { key: "radar", to: "/radar", label: "Radar", icon: Radio },
  { key: "notams", to: "/karte", label: "NOTAMs", icon: AlertCircle },
  { key: "logbuch", to: "/logbuch", label: "Logbuch", icon: Book },
  { key: "ndfilter", to: "/nd-filter", label: "ND-Filter", icon: Aperture },
  { key: "einstellungen", to: "/einstellungen", label: "Einstellungen", icon: Settings },
];

const TITLES: { match: RegExp; title: string; subtitle?: string }[] = [
  { match: /^\/$/, title: "COpter Pilot", subtitle: "Drohnen Pre-Flight Check" },
  { match: /^\/briefings\/[^/]+$/, title: "Briefing" },
  { match: /^\/briefings$/, title: "Briefings", subtitle: "Aktive & abgeschlossene Pre-Flight Checks" },
  { match: /^\/karte$/, title: "Karte", subtitle: "Geozonen · NOTAMs · Tieffluggebiete" },
  { match: /^\/naturschutz$/, title: "Naturschutzgebiete", subtitle: "BfN-Schutzgebiete" },
  { match: /^\/radar$/, title: "Live-Radar", subtitle: "Luftverkehr in Echtzeit" },
  { match: /^\/logbuch\/[^/]+$/, title: "Logbuch-Eintrag" },
  { match: /^\/logbuch$/, title: "Logbuch", subtitle: "Alle absolvierten Flüge" },
  { match: /^\/wetter$/, title: "Wetter", subtitle: "Live-Wetter & Flugwetter-Limits" },
  { match: /^\/drohnen\/[^/]+\/historie$/, title: "Drohnen-Historie" },
  { match: /^\/drohnen\/[^/]+\/info$/, title: "Drohnen-Info" },
  { match: /^\/drohnen\/[^/]+\/cinematic$/, title: "Cinematic-Einstellungen" },
  { match: /^\/drohnen\/[^/]+\/einrichten$/, title: "Drohne einrichten" },
  { match: /^\/drohnen$/, title: "Drohnen", subtitle: "Deine Flotte" },
  { match: /^\/nd-filter$/, title: "ND-Filter-Finder", subtitle: "Passenden Filter ermitteln" },
  { match: /^\/flugplanung\/[^/]+$/, title: "Flugplanung" },
  { match: /^\/flugplanung$/, title: "Flugplanung", subtitle: "Vorab-Planung für deinen nächsten Flug" },
  { match: /^\/fluege$/, title: "Flüge", subtitle: "Alle Pre-Flight Checks" },
  { match: /^\/flug\/[^/]+$/, title: "Flug" },
  { match: /^\/profil$/, title: "Profil", subtitle: "Pilot & Lizenz" },
  { match: /^\/ablauf$/, title: "Ablauf-Übersicht", subtitle: "Fristen & Gültigkeiten" },
  { match: /^\/akku\/[^/]+\/[^/]+$/, title: "Akku-Detail" },
  { match: /^\/glossar$/, title: "Glossar" },
  { match: /^\/ressourcen$/, title: "Ressourcen" },
  { match: /^\/laender\/[^/]+$/, title: "Länder-Info", subtitle: "Rules & Regulations" },
  { match: /^\/laender$/, title: "Länder-Info", subtitle: "Drohnenregeln in Europa" },
  { match: /^\/easa$/, title: "EASA-Referenz", subtitle: "EU-Klassen, Kategorien & Regeln" },
  { match: /^\/lizenzkarte$/, title: "Lizenzkarte", subtitle: "Piloten-Lizenz zum Vorzeigen" },
  { match: /^\/tcas$/, title: "TCAS-Einstellungen", subtitle: "Verkehrs- & Annäherungswarnungen" },
  { match: /^\/einstellungen$/, title: "Einstellungen" },
  { match: /^\/meine-daten$/, title: "Meine Daten" },
  { match: /^\/datenschutz$/, title: "Datenschutz" },
  { match: /^\/impressum$/, title: "Impressum" },
  { match: /^\/admin\/abos$/, title: "Abos & Pakete", subtitle: "Pakete, Preise & Rabattcodes" },
  { match: /^\/admin\/users\/[^/]+$/, title: "Nutzer-Detail" },
  { match: /^\/admin$/, title: "Admin", subtitle: "Nutzer- und Datenverwaltung" },
];

function activeTabFor(pathname: string): TabKey | null {
  if (pathname === "/" || pathname.startsWith("/dashboard")) return "briefings";
  if (pathname.startsWith("/briefings")) return "briefings";
  if (pathname.startsWith("/radar")) return "radar";
  if (pathname.startsWith("/karte") || pathname.startsWith("/naturschutz")) return "notams";
  if (pathname.startsWith("/logbuch")) return "logbuch";
  if (pathname.startsWith("/nd-filter")) return "ndfilter";
  return "einstellungen";
}

/* Sidebar-Navigation (nur Desktop): gruppierte Links, gespiegelt an den
   Tabs/Einstellungs-Einträgen, damit Web und App dieselben Bereiche bieten. */
type SidebarLink = {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  end?: boolean;
};
const SIDEBAR_GROUPS: { title: string; items: SidebarLink[] }[] = [
  {
    title: "Übersicht",
    items: [
      { to: "/", label: "Übersicht", icon: LayoutGrid, end: true },
      { to: "/briefings", label: "Briefings", icon: Clipboard },
      { to: "/karte", label: "Karte & NOTAMs", icon: Map },
      { to: "/naturschutz", label: "Naturschutzgebiete", icon: Trees },
      { to: "/radar", label: "Live-Radar", icon: Radar },
      { to: "/wetter", label: "Wetter", icon: CloudSun },
    ],
  },
  {
    title: "Planung & Flüge",
    items: [
      { to: "/flugplanung", label: "Flugplanung", icon: CalendarClock },
      { to: "/fluege", label: "Flüge", icon: ListChecks },
      { to: "/logbuch", label: "Logbuch", icon: BookOpen },
      { to: "/drohnen", label: "Drohnen", icon: Plane },
    ],
  },
  {
    title: "Mehr",
    items: [
      { to: "/profil", label: "Profil", icon: User },
      { to: "/lizenzkarte", label: "Lizenzkarte", icon: IdCard },
      { to: "/ablauf", label: "Ablauf-Übersicht", icon: CalendarX },
      { to: "/glossar", label: "Glossar", icon: BookMarked },
      { to: "/laender", label: "Länder-Info", icon: Globe2 },
      { to: "/easa", label: "EASA-Referenz", icon: BookText },
      { to: "/nd-filter", label: "ND-Filter-Finder", icon: Aperture },
      { to: "/tcas", label: "TCAS-Einstellungen", icon: ShieldAlert },
      { to: "/ressourcen", label: "Ressourcen", icon: LinkIcon },
      { to: "/einstellungen", label: "Einstellungen", icon: Settings },
      { to: "/abo", label: "Abo", icon: CreditCard },
    ],
  },
  {
    title: "Administration",
    items: [
      { to: "/admin", label: "Admin", icon: Shield, adminOnly: true, end: true },
      { to: "/admin/abos", label: "Abos & Pakete", icon: CreditCard, adminOnly: true },
    ],
  },
];

function pageTitle(pathname: string): { title: string; subtitle?: string } {
  for (const t of TITLES) {
    if (t.match.test(pathname)) return { title: t.title, subtitle: t.subtitle };
  }
  return { title: "COpter Pilot" };
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const activeTab = activeTabFor(location.pathname);
  const isOnBriefings = activeTab === "briefings";
  const { title, subtitle } = useMemo(() => pageTitle(location.pathname), [location.pathname]);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="app-shell bg-[var(--color-app-bg-root)]">
      {/* Desktop-Seitenleiste (Liquid-Glass). Auf Handy-Browsern ausgeblendet,
         dort übernimmt die Bottom-Tab-Bar. */}
      <aside className="app-sidebar hidden md:flex" aria-label="Hauptnavigation">
        <NavLink to="/" className="sidebar-brand" end>
          <span className="sidebar-brand-icon" aria-hidden />
          <span className="min-w-0">
            <span className="block text-[16px] font-bold tracking-tight text-[var(--color-app-text)] truncate">
              COpter Pilot
            </span>
            <span className="block text-[12px] text-[var(--color-app-text-secondary)] truncate">
              Drohnen Pre-Flight
            </span>
          </span>
        </NavLink>

        <nav className="flex-1 mt-1">
          {SIDEBAR_GROUPS.map((group) => {
            const items = group.items.filter((it) => !it.adminOnly || user?.isAdmin);
            if (items.length === 0) return null;
            return (
              <div key={group.title}>
                <div className="sidebar-section">{group.title}</div>
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      className={({ isActive }) =>
                        `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                      }
                      data-testid={`side-${it.to.replace(/\//g, "-")}`}
                    >
                      <Icon size={19} strokeWidth={2} />
                      <span className="flex-1 min-w-0 truncate">{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="mt-2 pt-3 border-t border-[var(--color-app-border)]">
          <button
            onClick={() => navigate("/flugplanung")}
            className="btn-primary w-full mb-3"
            data-testid="side-new-check"
          >
            <Plus size={18} strokeWidth={2.5} />
            Neuer Flug
          </button>
          <div className="px-2 pb-2">
            <div className="text-[11px] uppercase tracking-wider text-[var(--color-app-text-tertiary)]">
              Angemeldet als
            </div>
            <div className="text-[14px] font-semibold text-[var(--color-app-text)] truncate">
              {user?.name || user?.email || "—"}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="sidebar-link w-full"
            data-testid="button-logout-desktop"
          >
            <LogOut size={19} style={{ color: "#ef4444" }} />
            <span className="flex-1 text-left" style={{ color: "#ef4444" }}>
              Abmelden
            </span>
          </button>
        </div>
      </aside>

      {/* Phone-Frame (Mobil) bzw. Inhaltsspalte (Desktop). */}
      <div className="app-frame flex-1 min-w-0">
        {/* iOS-Style Navigation Bar — nur Handy-Browser. */}
        <header className="ios-navbar md:hidden">
          <div className="ios-navbar-title">{title}</div>
        </header>

        {/* Subtitle (mobil) direkt unter der Navbar */}
        {subtitle ? (
          <div className="px-5 pt-2 pb-1 text-center md:hidden">
            <p className="text-[13px] text-[var(--color-app-text-secondary)]">
              {subtitle}
            </p>
          </div>
        ) : null}

        {/* Inhalt */}
        <main className="px-4 md:px-8 lg:px-10 pt-0 md:pt-0 pb-32 md:pb-12">
          {/* Desktop-Inhalts-Header (Titel + Untertitel) */}
          <div className="desktop-header hidden md:flex">
            <div className="min-w-0">
              <h1 className="desktop-header-title truncate">{title}</h1>
              {subtitle ? (
                <p className="desktop-header-subtitle truncate">{subtitle}</p>
              ) : null}
            </div>
          </div>

          <div className="pt-3 md:pt-1">
            <SubscriptionGate>
              <Outlet />
            </SubscriptionGate>
          </div>
        </main>

        {/* Floating Action Button — nur Handy, auf Briefings-Tab (Desktop hat
           den "Neuer Flug"-Button in der Seitenleiste). */}
        {isOnBriefings ? (
          <button
            onClick={() => navigate("/flugplanung")}
            className="app-fab md:hidden"
            title="Neuer Flug"
            data-testid="fab-new-check"
          >
            <Plus size={26} strokeWidth={2.5} />
          </button>
        ) : null}

        {/* Bottom Tab Bar (Liquid-Glass-Anmutung) — nur Handy-Browser,
           sechs Tabs wie in der nativen App. */}
        <nav className="app-tabbar md:hidden" role="tablist">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.key}
                to={tab.to}
                className={`app-tab ${isActive ? "app-tab-active" : ""}`}
                data-testid={`tab-${tab.key}`}
                role="tab"
                aria-selected={isActive}
              >
                <Icon size={22} strokeWidth={isActive ? 2.4 : 1.9} />
                <span className="app-tab-label">{tab.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
